import { useState, useEffect, ChangeEvent } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { Field } from "@atlaskit/form";
import TextField from "@atlaskit/textfield";
import { token } from "@atlaskit/tokens";

import {
    InputIcon,
    useAccountCreationContext
} from "@/components/signup/account-creation";
import { emailIsValid, fontSize } from "@/components/signup/utils";
import { useUserContext } from "@/components/user-context";
import { Button } from "@/libraries/design/components";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";
import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";
export const InputSubmitButton = () => {
    const intl = useIntl();
    const {
        formError,
        hasSubmitted,
        isReadyForInvalid,
        isSubmitting,
        setIsEmailValid,
        setIsReadyForInvalid,
        setTypeAheadEmail,
        typeAheadEmail
    } = useAccountCreationContext();
    const [isValid, setValid] = useState<boolean>();
    const [email, setEmail] = useState(typeAheadEmail);

    useEffect(() => {
        const isValid = emailIsValid(email);

        // wait to show invalid state if the user is not finished typing
        if (!isValid && !isReadyForInvalid) {
            setValid(undefined);
            return;
        }

        setValid(isValid);
        setIsEmailValid(isValid);
    }, [email, setIsEmailValid, isReadyForInvalid]);

    const buttonText = intl.formatMessage({
        id: "signupForm.accountCreation.signupButton",
        defaultMessage: "Sign up"
    });

    const helperText = intl.formatMessage({
        id: "signupForm.accountCreation.helperText",
        defaultMessage:
            "Using a work email helps find teammates and boost collaboration."
    });

    const placeholderText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.emailPlaceholder",
        defaultMessage: "you@company.com"
    });

    const errorMessageText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.invalidEmail",
        defaultMessage: "Please enter a valid email address."
    });

    const labelText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.workEmail",
        defaultMessage: "Work email"
    });

    const productName = useUserContext().product?.key as ProductKeyTypes;

    const modalVisualUpdates = checkFeatureFlagGate(
        featureFlags.cfind4831ProductOverlayModalVisualUpdates
    );

    const inheritColorHelpText = checkFeatureFlagGate(
        featureFlags.signupAccountCreationInheritTextColor
    );

    return (
        <>
            <div css={containerCss}>
                <div css={[inputCss, modalVisualUpdates && inputV2Css]}>
                    <Field
                        aria-required={true}
                        isRequired
                        label={<span css={labelChildCss}>{labelText}</span>}
                        name="email"
                    >
                        {({ fieldProps }) => (
                            <div
                                css={[
                                    isValid === true && inputValidCss,
                                    isValid === false && inputInvalidCss
                                ]}
                            >
                                <TextField
                                    {...fieldProps}
                                    autoComplete="email"
                                    aria-describedby="account_creation_email_help_text"
                                    elemAfterInput={
                                        <InputIcon valid={isValid} />
                                    }
                                    onBlur={() => setIsReadyForInvalid(true)}
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement>
                                    ) => {
                                        setEmail(e.target.value);
                                        setTypeAheadEmail(e.target.value);
                                        fieldProps.onChange(e);
                                    }}
                                    placeholder={placeholderText}
                                    testId="email_field"
                                    type="email"
                                    value={email}
                                />
                            </div>
                        )}
                    </Field>
                </div>
                <div>
                    <Button
                        type="submit"
                        css={[buttonCss, modalVisualUpdates && buttonV2Css]}
                        isDisabled={
                            isSubmitting || (hasSubmitted && !formError)
                        }
                        isLoading={isSubmitting || (hasSubmitted && !formError)}
                        dataEventProps={{
                            "data-product-key": productName,
                            "data-event": "clicked",
                            "data-event-component": "linkButton",
                            "data-event-container": "signup",
                            "data-label": "sign-up",
                            "data-label-english": "Sign up"
                        }}
                    >
                        {buttonText}
                    </Button>
                </div>
            </div>
            {isValid === false ? (
                <div
                    css={errorMessageCss}
                    id="account_creation_email_help_text"
                    role="alert"
                >
                    {errorMessageText}
                </div>
            ) : helperText ? (
                <div
                    css={[
                        !inheritColorHelpText && helpTextCss,
                        inheritColorHelpText && inheritColorHelpTextCss
                    ]}
                    id="account_creation_email_help_text"
                >
                    {helperText}
                </div>
            ) : null}
        </>
    );
};

const containerCss = css({
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 8,
    marginBlockEnd: 16
});

const inputCss = css({
    width: "100%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div"]: {
        borderRadius: "21px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["input"]: {
        paddingInlineStart: "12px !important",
        fontFamily: "inherit",
        backgroundColor: `${token("elevation.surface")} !important`,
        fontSize: fontSize.md
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["label"]: {
        fontFamily: "inherit",
        color: "inherit",
        fontWeight: 500,
        overflow: "visible",
        width: 0,
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- hide asterisk
        ["[aria-hidden=true]"]: {
            display: "none"
        }
    }
});

const inputV2Css = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["div"]: {
        borderWidth: token("border.width", "1px")
    }
});

const labelChildCss = css({
    display: "inline-block",
    maxWidth: "calc(100vw - 48px)",
    width: 400
});

const buttonCss = css({
    fontSize: 18,
    height: 45.12, // to match the height of the input field
    whiteSpace: "nowrap"
});

// todo remove !important when we productionize gate and merge properties with buttonCss
const buttonV2Css = css({
    fontFamily: "inherit",
    fontSize: "16px !important",
    minWidth: "auto",
    paddingBlock: "8px !important"
});

const supportingText = css({
    margin: "0 auto",
    textAlign: "center",
    fontSize: fontSize.xs,
    lineHeight: 1.5
});

const helpTextCss = css({
    ...supportingText,
    color: token("color.text.subtlest")
});

const inheritColorHelpTextCss = css({
    ...supportingText,
    opacity: 0.8
});

const errorMessageCss = css({
    ...supportingText,
    color: token("color.icon.accent.red")
});

const inputInvalidCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> div": {
        borderColor: `${token("color.border.danger")} !important`
    }
});

const inputValidCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "> div": {
        borderColor: `${token("color.border.success")} !important`
    }
});
